// components/StatusPanel.jsx
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Chip,
  Badge,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Circle,
  People,
  Notifications,
} from '@mui/icons-material';

const StatusPanel = ({
  isConnected,
  isConnecting,
  disconnectReason,
  onlineCount,
  notifications,
  lastActivity,
  onConnect,
  onDisconnect,
  onShowNotifications,
  username,
}) => {
  return (
    <Paper
      sx={{
        width: 150,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: isConnected 
          ? 'rgba(0,0,0,0.7)' 
          : 'rgba(255,255,255,0.95)',
        border: `1px solid ${isConnected ? 'rgba(255,0,0,0.3)' : 'rgba(0,0,0,0.1)'}`,
        borderRadius: 2,
        color: isConnected ? 'white' : 'inherit',
      }}
    >
      {/* 標題區域 */}
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${isConnected ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'}`,
          backgroundColor: isConnected 
            ? 'rgba(255,0,0,0.2)' 
            : 'rgba(25,118,210,0.1)',
          display: 'flex',
          justifyContent: 'center',  // ✅ 水平居中
          alignItems: 'center',      // ✅ 垂直居中
        }}
      >
        <Box
          component="img"
          src="/warback.png"
          alt="戰情室"
          sx={{
            maxHeight: 64,
            maxWidth: '100%',
            objectFit: 'contain',
          }}
        />
      </Box>

      {/* 狀態資訊 */}
      <Box sx={{ p: 2, flex: 1 }}>
        <Stack spacing={2}>
          {/* 連線控制 */}
          <Box>
            <Typography 
              variant="subtitle2" 
              gutterBottom
              sx={{ color: isConnected ? 'rgba(255,255,255,0.9)' : 'text.primary' }}
            >
              連線控制
            </Typography>
            {!isConnected ? (
              <Button
                variant="contained"
                fullWidth
                onClick={onConnect}
                disabled={isConnecting}
                startIcon={isConnecting ? <CircularProgress size={16} /> : <Circle />}
                sx={{
                  backgroundColor: 'primary.main',
                  '&:hover': { backgroundColor: 'primary.dark' }
                }}
              >
                {isConnecting ? '連線中...' : '接入'}
              </Button>
            ) : (
              <Button
                variant="outlined"
                fullWidth
                onClick={onDisconnect}
                sx={{
                  color: isConnected ? 'rgba(255,255,255,0.8)' : 'inherit',
                  borderColor: isConnected ? 'rgba(255,255,255,0.3)' : 'inherit',
                  '&:hover': {
                    backgroundColor: 'rgba(255,0,0,0.1)',
                    borderColor: 'rgba(255,0,0,0.5)',
                  }
                }}
              >
                斷開
              </Button>
            )}
          </Box>

          {/* 連線狀態 */}
          <Box>
            <Typography 
              variant="subtitle2" 
              gutterBottom
              sx={{ color: isConnected ? 'rgba(255,255,255,0.9)' : 'text.primary' }}
            >
              連線狀態
            </Typography>
            <Chip
              icon={<Circle sx={{ fontSize: 12 }} />}
              label={
                isConnecting ? '連線中...' : 
                isConnected ? '已連線' : 
                disconnectReason ? '已斷線' : '未連線'
              }
              size="small"
              color={
                isConnecting ? 'warning' :
                isConnected ? 'success' : 
                disconnectReason ? 'error' : 'default'
              }
              variant={isConnected ? 'filled' : 'outlined'}
              sx={{ 
                width: '100%',
                backgroundColor: isConnected ? 'rgba(76,175,80,0.8)' : undefined,
                color: isConnected ? 'white' : undefined,
              }}
            />
          </Box>
          
          {/* 線上人數 */}
          <Box>
            <Typography 
              variant="subtitle2" 
              gutterBottom
              sx={{ color: isConnected ? 'rgba(255,255,255,0.9)' : 'text.primary' }}
            >
              當前在線
            </Typography>
            <Chip
              icon={<People />}
              label={`${onlineCount} 人`}
              variant={isConnected ? 'filled' : 'outlined'}
              color="primary"
              sx={{ 
                width: '100%',
                backgroundColor: isConnected ? 'rgba(25,118,210,0.8)' : undefined,
                color: isConnected ? 'white' : undefined,
              }}
            />
          </Box>

          {/* 通知區域 */}
          <Box>
            <Typography 
              variant="subtitle2" 
              gutterBottom
              sx={{ color: isConnected ? 'rgba(255,255,255,0.9)' : 'text.primary' }}
            >
              消息通知
            </Typography>
            <Button
              variant={isConnected ? 'contained' : 'outlined'}
              fullWidth
              startIcon={
                <Badge badgeContent={notifications.length} color="error">
                  <Notifications />
                </Badge>
              }
              onClick={onShowNotifications}
              sx={{
                backgroundColor: isConnected ? 'rgba(255,152,0,0.8)' : undefined,
                color: isConnected ? 'white' : undefined,
                borderColor: isConnected ? 'rgba(255,255,255,0.3)' : undefined,
                '&:hover': {
                  backgroundColor: isConnected ? 'rgba(255,152,0,0.9)' : undefined,
                }
              }}
            >
              查看
            </Button>
          </Box>

          <Divider sx={{ borderColor: isConnected ? 'rgba(255,255,255,0.2)' : undefined }} />

          {/* 戰情室啟動狀態 - 僅在連線時顯示 */}
          {isConnected ? (
            <>
              <Box>
                <Typography 
                  variant="subtitle2" 
                  gutterBottom 
                  sx={{ color: 'rgba(255,255,255,0.9)' }}
                >
                  戰情室已啟動
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ color: 'rgba(255,255,255,0.7)' }}
                >
                  你好，{username}，安全連線已建立，請開始情報交流...
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'block',
                    color: 'rgba(255,255,255,0.5)',
                    mt: 1,
                    fontSize: '0.7rem'
                  }}
                >
                  上次活動：{new Date(lastActivity).toLocaleTimeString('zh-TW')}
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <Box>
                <Typography 
                  variant="subtitle2" 
                  gutterBottom 
                  sx={{ color: 'rgba(241, 40, 40, 0.9)' }}
                >
                  戰情室待啟動
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ color: 'rgba(252, 85, 8, 0.7)' }}
                >
                  歡迎光臨，{username}，這裡是24小時不間斷聊天室，主要分享政治經濟相關的猛料，點擊上方接入按鈕開啟你的情報之旅吧...
                </Typography>
              </Box>
            </>
          )}
        </Stack>
      </Box>
    </Paper>
  );
};

export default StatusPanel;